import { useTranslation } from "react-i18next";
import { useState } from "react";
import { FormPreview } from "../../components/form-preview";
import { IQdsRequest, IQdsSignature, IQdsRequestMetadata, IDigitalSignatureData } from "../../entities/IQdsRequest";
import { MetaDataForm } from "../../entities/IMetaData";
import { GenericForm } from "../../components/generic-form/GenericForm.types";
import { Badge } from "../../components/badge";
import { SigningOrder } from "./useQdsSignDetail";
import "./QdsMetadataPreview.css";

interface QdsMetadataPreviewProps {
  qdsRequest: IQdsRequest | null;
  onResendEmail?: (signatureId: number, signatureType: "consent" | "holder" | "counterparty", email: string) => Promise<void>;
  signingOrder?: SigningOrder;
  currentUserId?: number;
  nextSignerEmail?: string | null;
  nextSignerPrio?: number | null;
}

interface SignatureRowProps {
  signature: IQdsSignature;
  signatureType?: "consent" | "holder" | "counterparty";
  onResendEmail?: (signatureId: number, signatureType: "consent" | "holder" | "counterparty", email: string) => Promise<void>;
  isUserTurn?: boolean;
  nextPrio?: number | null;
  canCurrentUserSign?: boolean;
}

function SignatureRow({ signature, signatureType, onResendEmail, isUserTurn, canCurrentUserSign }: SignatureRowProps) {
  const [isResending, setIsResending] = useState(false);
  const { t } = useTranslation();

  const handleResendClick = async () => {
    if (!onResendEmail || !signatureType) return;
    
    try {
      setIsResending(true);
      await onResendEmail(signature.id, signatureType, signature.email);
    } finally {
      setIsResending(false);
    }
  };

  const isTokenExpired = (): boolean => {
    if (signatureType !== "counterparty" || !signature.tokenValidUntil) {
      return false;
    }
    const tokenValidUntilDate = new Date(signature.tokenValidUntil);
    const now = new Date();
    return tokenValidUntilDate < now;
  };
  const getStatusType = (status: string): "primary" | "success" | "danger" | "secondary" | "warning" => {
    switch (status.toLowerCase()) {
      case "pending":
        return "warning";
      case "approved":
      case "signed":
        return "success";
      case "rejected":
        return "danger";
      default:
        return "secondary";
    }
  };

  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  return (
    <div className="signature-row">
      <div className="signature-row-left">
        <Badge 
          type={getStatusType(signature.status)}
          color={signature.status.toLowerCase() === "pending" ? "#e67e22" : undefined}
          className="signature-status-badge"
        >
          {signature.status}
        </Badge>
        <span className="signature-email">{signature.email}</span>
      </div>
      {isUserTurn && (signatureType === "holder" || signatureType === "counterparty") && (
        <div className="signature-row-middle">
          {isTokenExpired() ? (
            <div className="signature-turn-indicator signature-turn-indicator-expired">
              <span className="turn-circle turn-circle-expired"></span>
              <span className="turn-text">{t("QDS_REQUEST.SIGNATURE_EXPIRED")}</span>
            </div>
          ) : (
            <div className="signature-turn-indicator">
              <span className="turn-circle"></span>
              <span className="turn-text">{t("QDS_REQUEST.TURN_TO_SIGN")}</span>
            </div>
          )}
        </div>
      )}
      <div className="signature-row-right">
        {signature.status.toLowerCase() === "pending" && isUserTurn ? (
          <button
            className="btn btn-sm btn-outline-primary rounded-circle"
            style={{ width: "28px", height: "28px", padding: "0", display: "flex", alignItems: "center", justifyContent: "center" }}
            title="Resend"
            onClick={handleResendClick}
            disabled={isResending}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
              <path fillRule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z"/>
              <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z"/>
            </svg>
          </button>
        ) : signature.signedAt ? (
          <span className="signature-date">{formatDate(signature.signedAt)}</span>
        ) : (
          <span className="signature-date">-</span>
        )}
      </div>
    </div>
  );
}

interface SignatureSectionProps {
  title: string;
  emptyLabel?: string;
  signatures: IQdsSignature[] | undefined;
  signatureType?: "consent" | "holder" | "counterparty";
  onResendEmail?: (signatureId: number, signatureType: "consent" | "holder" | "counterparty", email: string) => Promise<void>;
  signingOrder?: SigningOrder;
  nextSignerEmail?: string | null;
  nextSignerPrio?: number | null;
}

function SignatureSection({ title, emptyLabel, signatures, signatureType, onResendEmail, signingOrder, nextSignerEmail, nextSignerPrio }: SignatureSectionProps) {
  // Sort signatures by priority for holder and counterparty (not for consent)
  const sortedSignatures = 
    signatures && (signatureType === "holder" || signatureType === "counterparty")
      ? [...signatures].sort((a, b) => (a.prio || 0) - (b.prio || 0))
      : signatures;

  return (
    <div className="signature-section">
      <h6 className="signature-section-title">{title}</h6>
      <div className="signature-list">
        {sortedSignatures && sortedSignatures.length > 0 ? (
          sortedSignatures.map((sig, index) => {
            // Check if this signature is the next in line
            const isNextSigner = sig.email === nextSignerEmail && (signatureType === "holder" || signatureType === "counterparty");
            // Check if current user is the next signer
            const canCurrentUserSign = isNextSigner && signingOrder?.canSign;
            
            return (
              <SignatureRow 
                key={index} 
                signature={sig} 
                signatureType={signatureType}
                onResendEmail={onResendEmail}
                isUserTurn={isNextSigner}
                nextPrio={nextSignerPrio}
                canCurrentUserSign={canCurrentUserSign}
              />
            );
          })
        ) : (
          <div className="signature-empty">Nema {emptyLabel ?? title.toLowerCase()}</div>
        )}
      </div>
    </div>
  );
}

interface DigitalSignatureCardProps {
  signature: IDigitalSignatureData;
}

function DigitalSignatureCard({ signature }: DigitalSignatureCardProps) {
  const { t } = useTranslation();
  
  const formatDateTime = (dateValue: string | Date | undefined): string => {
    if (!dateValue) return "-";
    const date = new Date(dateValue);
    return date.toLocaleString();
  };

  const fullName = [signature.firstName, signature.lastName].filter(Boolean).join(" ") || "-";

  return (
    <div className="digital-signature-card">
      <div className="digital-signature-field">
        <span className="digital-signature-label">{t("QDS_REQUEST.NAME")}:</span>
        <span className="digital-signature-value">{fullName}</span>
      </div>
      <div className="digital-signature-field">
        <span className="digital-signature-label">{t("QDS_REQUEST.PIB")}:</span>
        <span className="digital-signature-value">{signature.pib || "-"}</span>
      </div>
      <div className="digital-signature-field">
        <span className="digital-signature-label">{t("QDS_REQUEST.JMBG")}:</span>
        <span className="digital-signature-value">{signature.jmbg || "-"}</span>
      </div>
      <div className="digital-signature-field">
        <span className="digital-signature-label">{t("QDS_REQUEST.SIGNING_DATE")}:</span>
        <span className="digital-signature-value">{formatDateTime(signature.signingTime)}</span>
      </div>
    </div>
  );
}

export function QdsMetadataPreview({ qdsRequest, onResendEmail, signingOrder, currentUserId, nextSignerEmail, nextSignerPrio }: QdsMetadataPreviewProps) {
  const { t } = useTranslation();

  if (!qdsRequest) {
    return null;
  }

  // Nađi ime korisnika koji je kreirao zahtev
  const getCreatorName = (): string => {
    // Čitaj iz qdsRequest.document.metaData.qds.createdByUser
    const metaData = qdsRequest.document?.metaData;
    if (metaData?.qds) {
      const qdsData = typeof metaData.qds === 'string' 
        ? JSON.parse(metaData.qds) 
        : metaData.qds;
      
      if (qdsData?.createdByUser) {
        return String(qdsData.createdByUser);
      }
    }
    
    // Fallback na createdBy ako qds.createdByUser ne postoji
    if (qdsRequest.createdBy) {
      return `${qdsRequest.createdBy.firstName} ${qdsRequest.createdBy.lastName}`;
    }
    
    // Ako createdBy nije dostupan, probaj da nađeš iz holderSignatures
    const holderSignature = qdsRequest.holderSignatures?.find(
      (sig) => sig.userId === qdsRequest.createdById
    );
    
    if (holderSignature?.email) {
      return holderSignature.email;
    }
    
    // Ako ni to ne radi, probaj consents
    const consent = qdsRequest.consents?.find(
      (sig) => sig.userId === qdsRequest.createdById
    );
    
    return consent?.email || "-";
  };

  // Helper function to format status with badge styling
  const formatStatus = (status: string): string => {
    return status[0]?.toUpperCase() + status.slice(1);
  };

  // Helper function to format date
  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  // Build metadata form object
  const metaDataForm: GenericForm<MetaDataForm> = {
    createdBy: {
      label: t("QDS_REQUEST.CREATED_BY"),
      defaultValue: getCreatorName(),
      type: "input",
      name: "createdBy",
    },
    createdOn: {
      label: t("QDS_REQUEST.CREATED_ON"),
      defaultValue: formatDate(qdsRequest.createdAt),
      type: "input",
      name: "createdOn",
    },
    status: {
      label: t("FILTERS.DOCUMENT_STATUS") || "Document status",
      defaultValue: formatStatus(qdsRequest.status),
      type: "input",
      name: "status",
    },
  };

  return (
    <div className="d-flex flex-column">
      <div>
        <h5>{t("DOCUMENTS.QDS_DATA") || "QDS Signing Data"}</h5>
        <FormPreview metaDataForm={metaDataForm} />
      </div>

      <SignatureSection 
        title={t("QDS_REQUEST.APPROVALS")}
        signatures={qdsRequest.consents}
        signatureType="consent"
        onResendEmail={onResendEmail}
        signingOrder={signingOrder}
      />
      
      <SignatureSection
        title={t("QDS_REQUEST.HOLDER_SIGNATURES")}
        emptyLabel="potpisa za vlasnika"
        signatures={qdsRequest.holderSignatures}
        signatureType="holder"
        onResendEmail={onResendEmail}
        signingOrder={signingOrder}
        nextSignerEmail={nextSignerEmail}
        nextSignerPrio={nextSignerPrio}
      />
      
      <SignatureSection
        title={t("QDS_REQUEST.COUNTERPARTY_SIGNATURES")}
        emptyLabel="potpisa za drugu stranu"
        signatures={qdsRequest.counterPartySignatures}
        signatureType="counterparty"
        onResendEmail={onResendEmail}
        signingOrder={signingOrder}
        nextSignerEmail={nextSignerEmail}
        nextSignerPrio={nextSignerPrio}
      />

      {/* Digital Signatures Data Section */}
      <div className="signature-section">
        <h6 className="signature-section-title">{t('QDS_REQUEST.DIGITAL_SIGNATURE_DATA')}</h6>
        <div className="signature-list">
          {qdsRequest?.signaturesData && qdsRequest.signaturesData.length > 0 ? (
            qdsRequest.signaturesData.map((signature: IDigitalSignatureData, index: number) => (
              <DigitalSignatureCard key={index} signature={signature} />
            ))
          ) : (
            <div className="text-muted">{t('QDS_REQUEST.NO_DIGITAL_SIGNATURES')}</div>
          )}
        </div>
      </div>
    </div>
  );
}
